import Drawer from '@material-ui/core/Drawer';
import {Button, CalloutIcon, IconButton} from '@wandb/components';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import copyToClipboard from 'copy-to-clipboard';
import numeral from 'numeral';
import React, {useCallback, useContext, useState} from 'react';
import {Link} from 'react-router-dom';

import {envIsPublicCloud} from '../../config';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {DateFormat, format} from '../../util/date';
import * as urls from '../../util/urls';
import {Alias} from '../ArtifactAlias';
import {InlineMarkdownEditor} from '../elements/InlineMarkdownEditor';
import {
  checkRegistryProject,
  resolveRegistryUrl,
} from '../Registries/common/utils';
// TODO: change to common component once thats released
import {ResizableDrawer} from '../Registries/workflows/ResizableDrawer';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
import {useArtifactOverviewQueryCaller} from './queries/artifactOverviewDag.query';

export type artifactMembershipNodeInputType = {
  artifactID: string;
  artifactVersionIndex: number;
  artifactType: string;
  artifactCommitHash: string;
};

type InfoDrawerArtifactsProps = {
  open: boolean;
  closeDrawer: () => void;
  artifactMembershipNodeInput: artifactMembershipNodeInputType;
};

export const InfoDrawerArtifacts = ({
  open,
  closeDrawer,
  artifactMembershipNodeInput,
}: InfoDrawerArtifactsProps) => {
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const {artifactID, artifactVersionIndex, artifactType, artifactCommitHash} =
    artifactMembershipNodeInput;

  const versionIndex = `v${artifactVersionIndex}`;
  const {artifact: artifactResults, loading} = useArtifactOverviewQueryCaller(
    artifactID,
    versionIndex
  );

  const artifactName = artifactResults.name;
  const noProjectAccess = artifactResults.projectName == null;
  const ttlDaysLeft = artifactResults.ttlDaysLeft;

  const createdBy = artifactResults?.createdBy;

  const username =
    artifactResults?.createdBy?.__typename === 'User' &&
    artifactResults?.createdBy?.username != null
      ? artifactResults?.createdBy?.username
      : '';

  const copyAlias = useCallback(
    (alias: string) => {
      const copyText = `${artifactName}:${alias}`;
      copyToClipboard(copyText);
      toast(`Copied alias '${copyText}' to clipboard`);
    },
    [artifactName]
  );

  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  if (loading) {
    return (
      <Loader
        name="lineage-dag-artifact-node-info"
        inline
        size="tiny"
        samplingRate={1}
      />
    );
  }

  if (!loading && artifactResults == null) {
    return null;
  }

  const aliases = new Set();
  const artifactIsLinkedToCollection =
    artifactResults?.artifactMemberships?.edges != null &&
    artifactResults?.artifactMemberships?.edges?.length > 1;

  return (
    <Drawer anchor="right" open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="h-full w-full px-24 pb-20 pt-60">
            <div className="flex h-full w-full flex-col">
              <div className="flex items-center">
                <div className="flex flex-row py-20 text-xl font-semibold leading-8">
                  <CalloutIcon size="sm" icon="versions-layers" color="blue" />

                  <div className="ml-[8px] flex break-all">
                    {`${artifactName}:v${artifactVersionIndex}`}
                  </div>
                </div>
                <div className="flex-1 text-right">
                  <IconButton
                    aria-label="Close artifact info drawer"
                    onPress={closeDrawer}
                    icon="close"
                    variant="ghost"
                  />
                </div>
              </div>

              <div className="-mx-20 h-1 bg-moon-250"></div>

              <div className="pb-300 w-full flex-1 overflow-y-auto p-4">
                <div className="mb-4 mt-24 flex items-center">
                  <div>
                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] whitespace-nowrap text-moon-550">
                          Full name
                        </div>
                      </div>
                      <div>
                        {noProjectAccess ? (
                          <div
                            key={artifactID}
                            className="break-all font-semibold">
                            {artifactName}:v{artifactVersionIndex}
                          </div>
                        ) : (
                          <Link
                            key={artifactID}
                            to={urls.artifact({
                              entityName: artifactResults?.entityName ?? '',
                              projectName: artifactResults?.projectName ?? '',
                              artifactTypeName: artifactType,
                              artifactCollectionName: artifactName ?? '',
                              artifactCommitHash,
                            })}>
                            <div className="break-all font-semibold text-teal-600">
                              {artifactResults?.entityName}/
                              {artifactResults?.projectName}/{artifactName}:v
                              {artifactVersionIndex}
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>

                    <div className="mb-[14px] flex">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] w-[136px] text-moon-550">
                          Type
                        </div>
                      </div>
                      <div className="break-all">{artifactType}</div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Aliases
                      </div>
                      <div>
                        <div className="gap-y-5px flex flex-wrap">
                          {artifactResults?.aliases.map(
                            ({alias}: {alias: string}) => {
                              if (aliases.has(alias)) {
                                return <></>;
                              }
                              aliases.add(alias);
                              return (
                                // eslint-disable-next-line react/jsx-key
                                <div key={artifactName + alias}>
                                  <Alias
                                    alias={{alias}}
                                    onClick={() => copyAlias(alias)}
                                  />
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Digest
                      </div>
                      <div>{artifactResults?.digest}</div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Created At
                      </div>
                      <div>
                        {artifactResults?.createdAt != null &&
                          format(
                            new Date(artifactResults?.createdAt),
                            DateFormat.MONTH_DAYTH_YEAR_24HOUR_MIN_SECONDS
                          )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] text-moon-550">
                          Description
                        </div>
                      </div>
                      <div
                        className={`break-words ${
                          artifactResults?.description == null
                            ? 'text-moon-400'
                            : 'black'
                        }`}>
                        {artifactResults?.description != null ? (
                          <InlineMarkdownEditor
                            readOnly={true}
                            serverText={artifactResults?.description}
                          />
                        ) : (
                          '--'
                        )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] whitespace-nowrap text-moon-550">
                          Created by run
                        </div>
                      </div>
                      <div className="break-all">
                        {createdBy != null &&
                        createdBy.__typename === 'Run' &&
                        createdBy.runProjectQuery != null ? (
                          <Link
                            key={`${createdBy.displayName}`}
                            to={urls.run({
                              entityName: createdBy.runProjectQuery.runEntity,
                              projectName: createdBy.runProjectQuery.runProject,
                              name: createdBy.runName,
                            })}>
                            <div className="font-semibold text-teal-600">
                              {createdBy.displayName}
                            </div>
                          </Link>
                        ) : username !== '' ? (
                          <Link
                            key={`${username}`}
                            to={urls.profilePage(username)}>
                            <div className="font-semibold text-teal-600">
                              {username}
                            </div>
                          </Link>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px]">
                        <div className="mr-[80px] whitespace-nowrap text-moon-550">
                          Linked to
                        </div>
                      </div>
                      <div className="break-all">
                        {artifactIsLinkedToCollection ? (
                          artifactResults?.artifactMemberships?.edges.map(
                            am => {
                              if (
                                am.node?.artifactCollection != null &&
                                am.node.artifactCollection.__typename ===
                                  'ArtifactPortfolio'
                              ) {
                                const ac = am.node.artifactCollection;
                                const {registryName, isRegistryProject} =
                                  checkRegistryProject(
                                    ac.linkProjectQuery?.linkProjectName ?? ''
                                  );
                                return (
                                  <Link
                                    key={ac.linkPortfolioName}
                                    to={resolveRegistryUrl(
                                      isRegistryProject,
                                      accountOrgName,
                                      registryName ?? 'model',
                                      {
                                        entityName:
                                          ac.linkProjectQuery?.linkEntityName ??
                                          '',
                                        projectName:
                                          ac.linkProjectQuery
                                            ?.linkProjectName ?? '',
                                        collectionName: ac.linkPortfolioName,
                                      },
                                      artifactResults?.entityName,
                                      'membership',
                                      `v${am.node.versionIndex}`,
                                      'overview'
                                    )}
                                    onClick={closeDrawer}>
                                    {
                                      <div className="font-semibold text-teal-600">
                                        {ac.linkPortfolioName}:v
                                        {am.node.versionIndex}
                                      </div>
                                    }
                                  </Link>
                                );
                              }
                              return null;
                            }
                          )
                        ) : (
                          <div className="text-moon-400">{'--'}</div>
                        )}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        TTL remaining
                      </div>
                      <div>
                        {ttlDaysLeft === null
                          ? 'Inactive'
                          : ttlDaysLeft > 0
                            ? ttlDaysLeft + ' days'
                            : ttlDaysLeft === 0
                              ? 'less than a day left'
                              : 'pending deletion'}
                      </div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Num Consumers
                      </div>
                      <div>{artifactResults?.usedCount}</div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Num of Files
                      </div>
                      <div>{artifactResults?.fileCount}</div>
                    </div>

                    <div className="flex pb-[14px]">
                      <div className="mr-[80px] w-[136px] text-moon-550">
                        Size
                      </div>
                      <div>{numeral(artifactResults?.size).format('0.0b')}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="-mx-20 h-1 bg-moon-250"></div>
              <Button mt={7} variant="primary" fullWidth onPress={closeDrawer}>
                Done
              </Button>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};
